/**
 * Geometry helpers for dependency-arrow SVG paths. Separated from the
 * overlay so the math is unit-testable.
 *
 * Routing follows the classic gantt "finish-to-start" style: every
 * arrow leaves the source on the right, approaches the target
 * *from the left* via a short horizontal stub, and is joined by
 * small rounded corners. MS Project, Asana, Linear and Smartsheet
 * all use this pattern — arrows feel like cables plugged into ports
 * rather than free-form curves.
 */

export interface LineEndpoints {
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

/** Arrowhead size in px (length of the triangle from base to tip). */
export const ARROW_SIZE = 8;

/** Corner radius for the rounded elbow joints. */
export const CORNER_R = 6;

/**
 * Horizontal approach length just before the target: the arrow always
 * runs straight into the target for this many pixels so the tip
 * aligns with the incoming edge and the penultimate corner has room
 * to round.
 */
export const APPROACH_PX = 12;

/**
 * Horizontal clearance outside the source bar used by the backward
 * wrap — the arrow extends right by this much before turning to loop
 * around. Mirrored on the target side for the final approach.
 */
const WRAP_H = 14;

/**
 * Fallback vertical sweep distance used only by backward same-row
 * wraps (where routing between rows is impossible because there's no
 * row gap).
 */
const WRAP_V = 18;

/**
 * Tiny overlap between the line stroke and the arrowhead base. The
 * stroke has a flat end cap at line-end; without a small overlap, a
 * sub-pixel gap can open up between the end of the stroke and the
 * back of the triangle — visible at high-DPI scales as a missing
 * link between the cable and the connector.
 */
const LINE_ARROW_OVERLAP = 1;

/**
 * Produces the SVG `d` attribute for a rounded-elbow dependency
 * arrow. The line is always shortened by {@link ARROW_SIZE} so it
 * ends at the back of the arrowhead — otherwise the 1 px stroke
 * pokes through the triangle's narrowing tip and looks like the
 * cable continues past the connector.
 *
 * Three topologies:
 *
 * 1. **Same row, forward** — a single straight horizontal segment.
 * 2. **Forward with vertical delta** — horizontal run out of the
 *    source, bend near the target, short head-on approach for the
 *    last {@link APPROACH_PX}. Two quarter-arc corners. Typical
 *    finish-to-start gantt visual.
 * 3. **Backward (target at/behind source)** — four-corner hook that
 *    routes through the gap *between* the two rows (midpoint Y)
 *    when rows differ, so the cable reads as going "between the
 *    bars" and never wraps below an unrelated bar. When rows are
 *    too close for a between-rows route, falls back to a sweep on
 *    the target's side.
 */
export function buildDependencyPath(ep: LineEndpoints): string {
  const { fromX, fromY, toX, toY } = ep;
  const r = CORNER_R;
  // Line terminates at the back of the arrowhead so the stroke
  // doesn't show through the triangle's narrowing tip. The tiny
  // overlap hides the stroke's end cap under the triangle base.
  const endX = toX - ARROW_SIZE + LINE_ARROW_OVERLAP;

  // Case 1 — straight horizontal.
  if (Math.abs(toY - fromY) < 0.5 && endX > fromX) {
    return `M${fromX},${fromY} L${endX},${toY}`;
  }

  // Case 2 — forward elbow near the target. Needs enough room
  // between the source and the penultimate corner
  // (APPROACH_PX + 2*r) for a proper rounded join.
  if (endX > fromX + APPROACH_PX + 2 * r) {
    const bendX = endX - APPROACH_PX;
    const dy = toY - fromY;
    const sy = dy >= 0 ? 1 : -1;
    const rr = Math.max(
      1,
      Math.min(r, Math.abs(bendX - fromX), APPROACH_PX, Math.abs(dy) / 2)
    );
    return [
      `M${fromX},${fromY}`,
      `L${bendX - rr},${fromY}`,
      `Q${bendX},${fromY} ${bendX},${fromY + sy * rr}`,
      `L${bendX},${toY - sy * rr}`,
      `Q${bendX},${toY} ${bendX + rr},${toY}`,
      `L${endX},${toY}`,
    ].join(' ');
  }

  // Case 3 — backward wrap. The sweep lane ideally sits halfway
  // between the two rows (between the bars), so the cable doesn't
  // have to loop around either bar. Falls back to a side-sweep when
  // the rows are too close for a between-rows route to fit both
  // quarter-arcs.
  const dy = toY - fromY;
  const sy = dy >= 0 ? 1 : -1;
  const minBetween = 2 * r + 4;
  const sweepY =
    Math.abs(dy) >= minBetween
      ? (fromY + toY) / 2
      : sy > 0
        ? Math.max(fromY, toY) + WRAP_V
        : Math.min(fromY, toY) - WRAP_V;
  const outX = fromX + WRAP_H;
  const inX = endX - WRAP_H;
  // Segment directions must be computed relative to the *sweep lane*,
  // not the overall dy, because the side-sweep fallback places
  // `sweepY` outside the [fromY, toY] range — the two vertical runs
  // travel in *opposite* directions in that case, while the
  // between-rows route has them travel in the same direction.
  const sy1 = sweepY > fromY ? 1 : -1;
  const sy2 = toY > sweepY ? 1 : toY < sweepY ? -1 : sy;

  return [
    `M${fromX},${fromY}`,
    `L${outX - r},${fromY}`,
    // Corner 1: horizontal → vertical (source → sweep lane).
    `Q${outX},${fromY} ${outX},${fromY + sy1 * r}`,
    `L${outX},${sweepY - sy1 * r}`,
    // Corner 2: vertical → horizontal (onto the sweep lane).
    `Q${outX},${sweepY} ${outX - r},${sweepY}`,
    `L${inX + r},${sweepY}`,
    // Corner 3: horizontal → vertical (sweep lane → target row).
    `Q${inX},${sweepY} ${inX},${sweepY + sy2 * r}`,
    `L${inX},${toY - sy2 * r}`,
    // Corner 4: vertical → horizontal (into the target).
    `Q${inX},${toY} ${inX + r},${toY}`,
    `L${endX},${toY}`,
  ].join(' ');
}

/**
 * Triangular arrowhead polygon terminating at `(toX, toY)`, pointing
 * left-to-right. Every route (forward and backward) finishes with a
 * horizontal segment landing on the target from the left, so a
 * rightward-pointing tip is correct for all cases.
 */
export function arrowHeadPoints(ep: LineEndpoints): string {
  const x = ep.toX;
  const y = ep.toY;
  return [
    `${x},${y}`,
    `${x - ARROW_SIZE},${y - ARROW_SIZE / 2}`,
    `${x - ARROW_SIZE},${y + ARROW_SIZE / 2}`,
  ].join(' ');
}
